import React from 'react';
import { Box } from '@mui/material';
import { FinanceProgramRateType } from '@trnsact/trnsact-shared-types';
import { RadioGroupField, MultiSelectWithCustomOptions, InterestInputField, SwitchField } from 'components/form';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { rateTypeOptions, termsOptions } from 'modules/desking/constants';
import { FinanceProgramForm } from 'modules/financeProgram/schema';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

interface Props {
  isReadOnly: boolean;
}

export const RateType = ({ isReadOnly }: Props) => {
  const { control, setValue } = useFormContext<FinanceProgramForm>();

  const secondStep = useWatch({ control, name: 'program.second' });

  const { fields: specifyRatesFields } = useFieldArray({ control, name: 'program.second.specifyRates' });
  const { fields: specifyLenderRatesFields } = useFieldArray({
    control,
    name: 'program.second.specifyLenderMarketRates',
  });

  const handleChangeTerms = (selected: (string | number)[]) => {
    const termsToString = selected.map(String);

    setValue('program.second.terms', termsToString, { shouldValidate: true });
    setValue(
      'program.second.specifyRates',
      termsToString.map(term => ({ term, rate: secondStep.rate ?? 0 }))
    );
    setValue(
      'program.second.specifyLenderMarketRates',
      termsToString.map(term => ({ term: Number(term), rate: secondStep.rate ?? 0 }))
    );
  };

  return (
    <>
      <RadioGroupField
        row
        label="Rate Type"
        control={control}
        options={rateTypeOptions}
        name="program.second.rateType"
        onChange={(event, value) => {
          if (value === FinanceProgramRateType.RateFactor) setValue('program.second.isSpecifyRate', true);
          setValue('program.second.rateType', value as FinanceProgramRateType);
        }}
        formControlProps={{
          disabled: isReadOnly,
        }}
      />

      <Box className="formRow">
        <MultiSelectWithCustomOptions
          isOptionsSorted
          control={control}
          options={termsOptions}
          name="program.second.terms"
          onChange={handleChangeTerms}
          selectProps={{ disabled: isReadOnly, label: 'Terms in Months' }}
          selectFormControlProps={{ required: true, variant: 'standard' }}
          inputProps={{ type: 'number', label: 'Abb custom term', placeholder: 'Enter your own term' }}
        />

        <RenderWithCondition condition={!secondStep.isSpecifyRate}>
          <InterestInputField
            control={control}
            label="Buy Rate (%)"
            name="program.second.rate"
            textFieldProps={{ required: true, fullWidth: true, disabled: isReadOnly }}
          />
        </RenderWithCondition>
      </Box>

      <Box>
        <SwitchField
          control={control}
          label="Specify rate per term"
          name="program.second.isSpecifyRate"
          disabled={isReadOnly || secondStep.rateType === FinanceProgramRateType.RateFactor}
          extraAction={() => {
            setValue('program.second.commonLenderMarketRate', null);
          }}
        />
      </Box>

      {!!secondStep.isSpecifyRate && !!specifyRatesFields?.length && (
        <>
          {specifyRatesFields.map((field, index) => (
            <Box className="formRow">
              <InterestInputField
                key={field.id}
                control={control}
                label={`Buy Rate ${secondStep.terms?.[index] ?? ''} months (%)`}
                name={`program.second.specifyRates.${index}.rate`}
                textFieldProps={{ required: true, fullWidth: true, disabled: isReadOnly }}
              />
              {secondStep.configureSubsidyCap && specifyLenderRatesFields[index] && (
                <InterestInputField
                  key={specifyLenderRatesFields[index].id}
                  control={control}
                  name={`program.second.specifyLenderMarketRates.${index}.rate`}
                  textFieldProps={{ required: true, fullWidth: true }}
                  label={`Lender Market Rate ${field?.term ?? ''} months (%)`}
                />
              )}
            </Box>
          ))}
        </>
      )}
    </>
  );
};
